import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, RotateCcw, AlertCircle, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const ComplianceStackGame = ({ id = "game" }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [gameState, setGameState] = useState("start"); // start, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const requestRef = useRef();

  // Game State Refs
  const blocks = useRef([]);
  const currentBlock = useRef({ x: 0, y: 0, width: 200, speed: 4, direction: 1 });
  const cameraY = useRef(0);
  const targetCameraY = useRef(0);

  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Refs for dynamic dimensions
  const canvasWidth = useRef(800);
  const canvasHeight = useRef(500);

  // Constants
  const BLOCK_HEIGHT = 40;

  useEffect(() => {
    const saved = localStorage.getItem("meraki_compliance_high_score");
    if (saved) setHighScore(parseInt(saved));

    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
    };

    const updateCanvasSize = () => {
      if (containerRef.current && canvasRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        canvasWidth.current = clientWidth * window.devicePixelRatio;
        canvasHeight.current = clientHeight * window.devicePixelRatio;
        canvasRef.current.width = canvasWidth.current;
        canvasRef.current.height = canvasHeight.current;
      }
    };

    // Force non-passive touchmove listener to prevent scrolling
    const container = containerRef.current;
    const preventDefault = (e) => {
      if (container && container.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (container) {
      container.addEventListener('touchmove', preventDefault, { passive: false });
    }

    checkTouch();
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (container) {
        container.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  const initGame = () => {
    const initialWidth = 300;
    blocks.current = [
      { x: (canvasWidth.current - initialWidth) / 2, y: canvasHeight.current - BLOCK_HEIGHT, width: initialWidth, color: "#c9a961" }
    ];
    currentBlock.current = { 
      x: 0, 
      y: canvasHeight.current - BLOCK_HEIGHT * 2, 
      width: initialWidth, 
      speed: 4, 
      direction: 1 
    };
    cameraY.current = 0;
    targetCameraY.current = 0;
    setScore(0);
    setGameState("playing");
  };

  const handleAction = (e) => {
    if (e) e.stopPropagation();
    if (gameState === "start" || gameState === "gameOver") {
      initGame();
      return;
    }

    if (gameState !== "playing") return;

    const lastBlock = blocks.current[blocks.current.length - 1];
    const cur = currentBlock.current;

    const diff = cur.x - lastBlock.x;
    const absDiff = Math.abs(diff);

    if (absDiff >= lastBlock.width) {
      setGameState("gameOver");
      return;
    }

    const newWidth = lastBlock.width - absDiff;
    const newX = diff > 0 ? cur.x : lastBlock.x;

    blocks.current.push({
      x: newX,
      y: cur.y,
      width: newWidth,
      color: "#c9a961"
    });

    setScore(s => s + 1);

    const nextY = cur.y - BLOCK_HEIGHT;
    currentBlock.current = {
      x: cur.direction === 1 ? 0 : canvasWidth.current - newWidth,
      y: nextY,
      width: newWidth,
      speed: Math.min(8, 4 + blocks.current.length * 0.2),
      direction: Math.random() > 0.5 ? 1 : -1
    };

    if (blocks.current.length > 5) {
      targetCameraY.current += BLOCK_HEIGHT;
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const update = () => {
      if (gameState !== "playing") return;

      const cur = currentBlock.current;
      cur.x += cur.speed * cur.direction;

      if (cur.x + cur.width > canvasWidth.current || cur.x < 0) {
        cur.direction *= -1;
      }

      cameraY.current += (targetCameraY.current - cameraY.current) * 0.1;
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvasWidth.current, canvasHeight.current);
      
      ctx.save();
      ctx.translate(0, cameraY.current);

      blocks.current.forEach((block, index) => {
        const opacity = Math.max(0.2, 1 - (blocks.current.length - index) * 0.1);
        ctx.fillStyle = `rgba(201, 169, 97, ${opacity})`;
        ctx.strokeStyle = "#c9a961";
        ctx.lineWidth = 2;
        
        ctx.fillRect(block.x, block.y, block.width, BLOCK_HEIGHT);
        ctx.strokeRect(block.x, block.y, block.width, BLOCK_HEIGHT);

        ctx.fillStyle = "rgba(0,0,0,0.1)";
        for (let i = 10; i < block.width - 10; i += 40) {
          ctx.fillRect(block.x + i, block.y + 15, 30, 2);
          ctx.fillRect(block.x + i, block.y + 25, 20, 2);
        }
      });

      if (gameState === "playing") {
        const cur = currentBlock.current;
        ctx.fillStyle = "#e4c77a";
        ctx.shadowBlur = 15;
        ctx.shadowColor = "rgba(228, 199, 122, 0.5)";
        ctx.fillRect(cur.x, cur.y, cur.width, BLOCK_HEIGHT);
        ctx.strokeStyle = "#fff";
        ctx.strokeRect(cur.x, cur.y, cur.width, BLOCK_HEIGHT);
        ctx.shadowBlur = 0;
      }

      ctx.restore();

      if (gameState === "playing") {
        requestRef.current = requestAnimationFrame(() => {
          update();
          draw();
        });
      } else {
        requestRef.current = requestAnimationFrame(draw);
      }
    };

    requestRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, score]);

  useEffect(() => {
    if (gameState === "gameOver" && score > highScore) {
      setHighScore(score);
      localStorage.setItem("meraki_compliance_high_score", score.toString());
    }
  }, [gameState, score, highScore]);

  return (
    <section id={id} className="py-24 relative overflow-hidden bg-[#080808]">
      <div className="container-xl px-4 md:px-6">
        <div className="flex flex-col mb-12">
          <div className="text-center md:text-left mb-8">
            <Kicker index="LGL-GAME" label="Interactive Compliance" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Compliance <span className="italic gold-text">Stack</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-[10px] md:text-sm uppercase tracking-[0.2em] max-w-lg mx-auto leading-relaxed">
              Build a solid agreement. Stack the clauses perfectly to ensure maximum compliance.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div 
          ref={containerRef}
          className="relative max-w-4xl mx-auto aspect-[3/4] md:aspect-[16/10] rounded-[32px] md:rounded-[40px] overflow-hidden border border-[rgba(201,169,97,0.15)] bg-black shadow-2xl group cursor-pointer touch-none"
          data-lenis-prevent
          onClick={handleAction}
          onTouchStart={handleAction}
          onWheel={(e) => e.stopPropagation()}
        >
          <canvas 
            ref={canvasRef}
            width={800}
            height={500}
            className="w-full h-full object-cover"
          />

          <AnimatePresence>
            {gameState === "start" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-30"
              >
                <div 
                  className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: "url('/images/compliance_stack_thumb.png')" }}
                />
                <div className="absolute inset-0 z-10 bg-black/80 backdrop-blur-[6px]" />

                <div className="relative z-20 flex flex-col items-center p-8 md:p-12 rounded-3xl bg-black/40 backdrop-blur-xl max-w-[85%] md:max-w-none">
                  <Layers className="text-[var(--gold)] w-12 h-12 md:w-16 md:h-16 mb-6 md:mb-8 animate-bounce" />
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-4 italic text-center leading-tight">Draft Agreement?</h3>
                  <p className="text-[var(--cream-muted)] text-[8px] md:text-[10px] tracking-[0.3em] uppercase mb-8 md:mb-10 text-center leading-relaxed">
                    {isTouchDevice ? "Tap" : "Click"} to drop the clause. <br /> Align perfectly to maintain strength.
                  </p>
                  <button 
                    className="btn-primary px-8 md:px-10 py-4" 
                    onClick={(e) => { e.stopPropagation(); initGame(); }}
                  >
                    Start Drafting
                  </button>
                </div>
              </motion.div>
            )}

            {gameState === "gameOver" && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/95 backdrop-blur-2xl z-30 p-6 text-center"
              >
                <AlertCircle className="text-red-500 w-12 h-12 md:w-20 md:h-20 mb-4 md:mb-6" />
                <h3 className="font-display text-3xl md:text-5xl text-white mb-2 tracking-tighter uppercase leading-tight">Legal Collapse</h3>
                <p className="text-red-400 font-impact text-[10px] md:text-sm tracking-[0.4em] uppercase mb-8 md:mb-12">Agreement Instability Detected</p>
                
                <div className="flex gap-8 md:gap-16 mb-10 md:mb-12">
                  <div className="text-center">
                    <div className="text-[8px] md:text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">CLAUSES STACKED</div>
                    <div className="text-3xl md:text-5xl font-display text-white">{score}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[8px] md:text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">RECORD</div>
                    <div className="text-3xl md:text-5xl font-display text-[var(--gold)]">{highScore}</div>
                  </div>
                </div>

                <button className="btn-primary px-8 md:px-12 py-4 md:py-5" onClick={(e) => { e.stopPropagation(); initGame(); }}>
                  <RotateCcw className="w-4 h-4 mr-2" /> Re-draft Terms
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* HUD */}
          {gameState === "playing" && (
            <div className="absolute top-10 right-10 text-right z-20 pointer-events-none">
              <div className="text-[10px] text-[var(--gold)] uppercase tracking-[0.3em] mb-1">STABILITY</div>
              <div className="text-3xl font-display text-white">{score}</div>
            </div>
          )}
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Compliance Stack"
        instructions={[
          { title: "Timing is Key", desc: "A legal clause moves back and forth across the screen. Click anywhere to drop it onto the existing stack." },
          { title: "Maintain Alignment", desc: "Align the moving clause with the one below it. Any part of the clause that hangs over the edge will be sliced off, making the next one smaller." },
          { title: "Build Stability", desc: "Successfully stacking clauses increases your 'Stability' score. The more clauses you stack, the faster the next one moves." },
          { title: "Avoid Collapse", desc: "If you miss the stack entirely, the agreement collapses and the mission fails. Aim for a high-stability contract!" }
        ]}
      />
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center justify-center md:justify-start gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default ComplianceStackGame;
